import { useMemo, useState } from "react";
import {
  Box,
  Command,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  CommandList,
  Text,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import {
  groupQuickOptions,
  matchesQuery,
  type QuickFilterOption,
  SEARCH_THRESHOLD,
} from "./quick-filter-options";
import { QuickOptionRow } from "./quick-option-row";


type QuickOptionListProps = Omittable<{
  options: QuickFilterOption[];
  value: unknown;
  onChange: (value: unknown) => void;
  /** The filter's name, for the search field's accessible name. The page
   *  header already says it on screen, so nothing here repeats it. */
  label: string;
}>;

/**
 * ONE quick filter's values, as the searchable multi-select list its row
 * drills into on a phone.
 *
 * Every pool reaches the reader through this one list — a member roster, the
 * follow-up states, a workflow and its stages — so the panel's root list holds
 * no roster and no per-row search field. The search appears only once the set
 * outgrows a glance; four named states get no field to skip past. It is
 * PINNED above the list, which scrolls under it: a roster of forty is found
 * by typing, not by thumbing.
 *
 * The list fills whatever height its page hands it — the popup's, less the
 * page header — so it must sit in a column that has one (`DrawerPanel
 * variant="workspace"`).
 *
 * The rows toggle in place and the list stays open: picking three assignees is
 * one visit, not three. A value comes off by unchecking it, or wholesale from
 * the page header's Clear.
 */
export function QuickOptionList({
  options,
  value,
  onChange,
  label,
}: QuickOptionListProps) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();
  const searchable = options.length > SEARCH_THRESHOLD;

  const groups = useMemo(
    () =>
      groupQuickOptions(
        trimmed ? options.filter((o) => matchesQuery(o, trimmed)) : options,
      ),
    [options, trimmed],
  );

  return (
    <Command
      // The highlight must not rewrite the query — this is a multi-press
      // surface, and the typed text is what the next press filters against.
      mode="none"
      autoHighlight={false}
      value={query}
      onValueChange={setQuery}
    >
      {searchable ? (
        <CommandInput inset aria-label={`Search ${label}`} placeholder="Search…" />
      ) : null}
      {/* The list's ScrollArea only scrolls against a definite height: this
          column takes the page's slack and hands it down. */}
      <Box direction="column" fill="flex" overflow="hidden">
        {groups.length === 0 ? (
          <Box paddingX="md" paddingY="md">
            <Text size="sm" variant="muted">
              {`No matches for “${query.trim()}”.`}
            </Text>
          </Box>
        ) : (
          <CommandList>
            {groups.map((group) => (
              <CommandGroup key={group.key}>
                {group.label ? (
                  <CommandGroupLabel>{group.label}</CommandGroupLabel>
                ) : null}
                {group.options.map((option) => (
                  <QuickOptionRow
                    key={option.key}
                    value={option.key}
                    option={option}
                    selected={option.selected(value)}
                    onToggle={() => onChange(option.toggle(value))}
                  />
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        )}
      </Box>
    </Command>
  );
}
